<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Notifications" />
    
    <div class="container space-y-4">

      <hr>
      
      <!-- General -->
      <div class="grid gap-6 p-6 lg:grid-cols-4">
        <div class="col-span-1">
          <h2 class="my-2 text-lg font-bold">
            General
          </h2>
        </div>
        
        
        <div class="col-span-3 space-y-6">
          <div class="card">
            <div class="p-6">
              <h3 class="text-lg font-bold">
                Enable Notifications
              </h3>
              <div class="mt-2 text-gray-500">
                Turn all your notifications on or off. If notifications are disabled, no notification emails or SMSs will be sent at all.
              </div>
              <div class="flex mt-4">
                <ButtonToggle
                  :enabled="account.notifications_enabled"
                  :status-text="account.notifications_enabled ? 'Notifications are Enabled' : 'Notifications are Disabled'"
                  :button-text="account.notifications_enabled ? 'Disable Notifications' : 'Enable Notifications'"
                  :updating="submitting" 
                  @toggle="toggleNotifications"
                  />
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr>
      
      <!-- Booking -->
      <div class="grid gap-6 p-6 lg:grid-cols-4">
        <div class="col-span-1">
          <h2 class="my-2 text-lg font-bold">
            Booking Notifications
          </h2>
          <p class="text-gray-500">
            These notifications are automatically sent out to the customer of a booking.
          </p>
        </div>
        <div class="col-span-3 space-y-6">
          <div class="card" v-for="notification in bookingNotifications" :key="notification.name">
            <div class="p-6">
              <h3 class="text-lg font-bold">
                {{ notification.name }}
              </h3>
              <div class="mt-2 text-gray-500">
                {{ notification.description }}
              </div>
              <div class="flex mt-3">
                <ButtonToggle
                  :enabled="settings[`${notification.id}_enabled`]"
                  :status-text="settings[`${notification.id}_enabled`] ? `Enabled` : `Disabled`"
                  :button-text="settings[`${notification.id}_enabled`] ? `Disable` : `Enable`"
                  :updating="submitting" 
                  @toggle="toggleSetting(notification)"
                  />
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr>

      <!-- Order -->
      <div class="grid gap-6 p-6 lg:grid-cols-4">
        <div class="col-span-1">
          <h2 class="my-2 text-lg font-bold">
            Order Notifications
          </h2>
          <p class="text-gray-500">
            These notifications are automatically sent out to the contact person of an order.
          </p>
        </div>
        <div class="col-span-3 space-y-6">
          <div class="card" v-for="notification in orderNotifications" :key="notification.name">
            <div class="p-6" @click="notification.open = !notification.open">
              <h3 class="text-lg font-bold">
                {{ notification.name }}
              </h3>
              <div class="mt-2 text-gray-500">
                {{ notification.description }}
              </div>
              <div class="flex mt-3">
                <ButtonToggle
                  :enabled="settings[`${notification.id}_enabled`]"
                  :status-text="settings[`${notification.id}_enabled`] ? `Enabled` : `Disabled`"
                  :button-text="settings[`${notification.id}_enabled`] ? `Disable` : `Enable`"
                  :updating="submitting" 
                  @toggle="toggleSetting(notification)"
                  />
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr>

      <!-- Staff -->
      <div class="grid gap-6 p-6 lg:grid-cols-4">
        <div class="col-span-1">
          <h2 class="my-2 text-lg font-bold">
            Staff Notifications
          </h2>
          <p class="text-gray-500">
            Choose how you want to be notified when a new order comes in or add other recipients.
          </p>
        </div>
        <div class="col-span-3 space-y-6">

          <div class="card">
            <div class="p-6">
              <h3 class="text-lg font-bold">
                Booking &amp; Order Updates
              </h3>
              <div class="mt-2 text-gray-500">
                Send these staff accounts' an email when a new booking is placed, an existing booking is cancelled, an order is updated, or feedback is sent:
              </div>
            </div>
            <div class="pb-3">
              <table class="min-w-full">
                <tbody>
                  <tr class="cursor-pointer hover:bg-gray-50" v-for="user in users" :key="user.id">
                    <td class="flex items-center py-4 pl-5 pr-6 font-bold whitespace-nowrap">
                      <avatar :username="user.name" :size="36" color="#fff" background-color="#718096" class="mr-4" :custom-style="{ 'font-weight': 'bold' }" /> 
                      {{ user.name }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      {{ user.email }}
                    </td>
                    <td class="px-6 py-4 text-right whitespace-nowrap">
                      <div class="flex items-center justify-end h-full">
                        <toggle 
                          :value="user.accounts[0].settings.send_booking_notifications" 
                          @change="toggleNotifiedUser(user)" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Avatar from 'vue-avatar'
import ButtonToggle from '@/components/ButtonToggle'

export default {
  name: 'NotificationSettings',

  metaInfo: {
    title: 'Notification Settings'
  },

  components: {
    Avatar,
    ButtonToggle
  },

  async created () {
    await this.fetchSettings()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      loading: true,
      submitting: false,
      users: [],
      settings: {},
      bookingNotifications: [
        {
          id: 'booking_received' ,
          name: 'Booking Received',
          description: 'Sent automatically to the customer after they place their order through a booking form.'
        },
        {
          id: 'booking_changed',
          name: 'Booking Changed',
          description: 'Sent to the customer when you make changes to their booking (if you choose to send the update).'
        },
        {
          id: 'booking_cancelled',
          name: 'Booking Cancelled',
          description: 'Sent automatically to the customer if their order is canceled (if you choose to send the update).'
        },
      ],
      orderNotifications: [
        {
          id: 'order_captured',
          name: 'Order Captured',
          description: 'Sent automatically to the contact of an order when their order is captured in the system.'
        },
        {
          id: 'order_assigned',
          name: 'Order Assigned',
          description: 'Sent automatically to the contact of an order when their order is sent to and accepted by a driver.'
        },
        {
          id: 'order_eta',
          name: 'Order ETA (Estimated Time of Arrival)',
          description: 'Sent automatically to the contact of an order when the driver has started the order, and is nearby.'
        },
        {
          id: 'order_complete',
          name: 'Order Complete',
          description: 'Sent automatically to the original customer of an order when their order is marked as complete.'
        },
      ],
      customerNotifications: [
        {
          name: 'Account Created',
          description: ''
        },
        {
          name: 'Password Reset',
          description: ''
        },
      ],
    }
  },

  methods: {
    async fetchSettings () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/notification-settings`)
        this.users = data.users.data
        this.settings = data.settings
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async toggleSetting (notification) {
      const switchTo = !this.settings[`${notification.id}_enabled`]

      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/notification-settings/${this.account.id}`, {
          [`${notification.id}_enabled`]: switchTo
        })
        this.fetchSettings()
        this.$toasted.show(`${notification.name} notifications ${switchTo ? 'enabled' : 'disabled'}.`)
      } catch (error) {
        console.error(error)
        // this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    async toggleNotifiedUser (user) {
      try {
        if (user.accounts[0].settings.send_booking_notifications) {
          await this.$api.delete(`account/${this.$route.params.account}/notified-users/${user.id}`)
          this.$toasted.show(`Staff notification disabled for ${user.email}`)
        } else {
          await this.$api.put(`account/${this.$route.params.account}/notified-users/${user.id}`)
          this.$toasted.show(`Staff notification enabled for ${user.email}`)
        }
        this.fetchSettings()
      } catch (error) {
        this.$toasted.error(error.response.data.message)
        console.error(error)
      }
    },

    async toggleNotifications () {
      const switchTo = !this.account.notifications_enabled

      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/accounts/${this.account.id}`, {
          ...this.account,
          notifications_enabled: switchTo
        })
        await this.$store.dispatch('syncUser', this)
        this.$toasted.show(`All notifications ${switchTo ? 'enabled' : 'disabled'}.`)
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>